import { useEffect, useRef, useState } from 'react';
import { Box, RichTextArea, styled } from 'shell/components';

const RichTextSubmissionViewer = ({ content, highlights = [], url }) => {
    const [value, setValue] = useState(content || '');
    const rootRef = useRef(null);

    useEffect(() => {
        if (content != null) {
            setValue(content);
            return;
        }
        if (!url) return;

        const controller = new AbortController();
        fetch(url, { signal: controller.signal })
            .then((response) => {
                if (!response.ok) throw new Error(`Unable to load text entry (${response.status})`);
                return response.text();
            })
            .then(setValue)
            .catch((error) => {
                if (error.name !== 'AbortError') console.error(error);
            });

        return () => controller.abort();
    }, [content, url]);

    useEffect(() => {
        if (!highlights.length) return;
        const frame = requestAnimationFrame(() => {
            rootRef.current
                ?.querySelector('.rich-text-submission-highlight')
                ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
        return () => cancelAnimationFrame(frame);
    }, [highlights, value]);

    return (
        <RootBox ref={rootRef}>
            <RichTextArea
                aria-label={'Text entry submission'}
                highlights={highlights}
                readOnly
                sx={{ height: '100%' }}
                value={value}
                variant={'static'}
            />
        </RootBox>
    );
};

const RootBox = styled(Box)(({ theme }) => ({
    height: '100%',
    boxSizing: 'border-box',
    overflow: 'hidden',
    '& .MuiRichTextArea-root': {
        height: '100%',
        minHeight: 0,
        maxHeight: 'none',
        border: 'none',
        borderRadius: 0,
        padding: 0,
        '&:hover, &:focus-within': {
            border: 'none',
            outline: 'none',
        },
    },
    '& .MuiRichTextArea-root > .MuiBox-root': {
        padding: 0,
        background: theme.palette.ensightful.shades[0],
    },
    '& .MuiRichTextArea-root .ProseMirror': {
        boxSizing: 'border-box',
        height: 'auto',
        minHeight: '100%',
        padding: theme.spacing(12, 16),
        [theme.breakpoints.down('desktop')]: {
            padding: theme.spacing(8, 10),
        },
        [theme.breakpoints.down('tablet')]: {
            padding: theme.spacing(4),
        },
    },
    '& .rich-text-submission-highlight': {
        borderRadius: '2px',
        background: theme.palette.ensightful.warning[200],
        boxDecorationBreak: 'clone',
        WebkitBoxDecorationBreak: 'clone',
    },
    '& .MuiRichTextArea-root .ProseMirror, & .MuiRichTextArea-root .ProseMirror p, & .MuiRichTextArea-root .ProseMirror li':
        {
            lineHeight: '1.9',
        },
}));

export default RichTextSubmissionViewer;
